import React, { useEffect, useState } from 'react';

import { Alert, Box, Fade, Stack, Tab, Tabs } from '@mui/material';

import LuxLoyaltyAccountOverview from '~/components/LuxLoyalty/LuxLoyaltyAccountOverview';
import LuxLoyaltyActivityLog from '~/components/LuxLoyalty/LuxLoyaltyActivityLog';
import LuxLoyaltyBenefitsOverview from '~/components/LuxLoyalty/LuxLoyaltyBenefitsOverview';
import LuxLoyaltyInfiniteOnboardingEmails from '~/components/LuxLoyalty/LuxLoyaltyInfiniteOnboardingEmails';

import type { EnrolledTier } from '~/hooks/apis/loyalty/useEnrolLuxLoyaltyAccount';

import { getLuxLoyaltyTierDisplayName } from '~/utils/luxLoyalty';

interface Props {
  account: App.LuxLoyaltyAccount;
  customerEmail: string;
  refreshData: () => Promise<void>;
  /** When set, renders a transient success Alert that self-clears after ~5s. */
  justEnrolledTier?: EnrolledTier | null;
}

const HIGHLIGHT_DURATION_MS = 5000;

type ViewTabKey = 'account-overview' | 'benefits-overview' | 'activities';

const VIEW_TABS: ReadonlyArray<{ label: string; value: ViewTabKey }> = [
  { label: 'Account', value: 'account-overview' },
  { label: 'Benefits', value: 'benefits-overview' },
  { label: 'Activities', value: 'activities' },
];

export default function LuxLoyaltyUserProfile({ account, customerEmail, refreshData, justEnrolledTier }: Props) {
  const [currentTab, setCurrentTab] = useState<ViewTabKey>(VIEW_TABS[0].value);
  const [showJustEnrolled, setShowJustEnrolled] = useState(!!justEnrolledTier);

  useEffect(() => {
    if (!justEnrolledTier) {
      setShowJustEnrolled(false);
      return;
    }
    setShowJustEnrolled(true);
    const timer = window.setTimeout(() => setShowJustEnrolled(false), HIGHLIGHT_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [justEnrolledTier]);

  const highlightLabel = (() => {
    if (!justEnrolledTier) {
      return '';
    }
    if (justEnrolledTier === 'default') {
      return `Newly enrolled — tier: ${getLuxLoyaltyTierDisplayName(account.tier)} (default)`;
    }
    return `Newly enrolled — tier: ${getLuxLoyaltyTierDisplayName(justEnrolledTier)}`;
  })();

  return (
    <Stack direction="column" gap={2}>
      <Fade in={showJustEnrolled} unmountOnExit>
        <Alert severity="success">{highlightLabel}</Alert>
      </Fade>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTab} onChange={(_, next) => setCurrentTab(next as ViewTabKey)}>
          {VIEW_TABS.map((view) => (
            <Tab key={view.value} value={view.value} label={view.label} />
          ))}
        </Tabs>
      </Box>
      {currentTab === 'account-overview' && (
        <>
          <LuxLoyaltyAccountOverview account={account} onAccountUpdated={refreshData} />
          {account.tier === 'infinite' && (
            <LuxLoyaltyInfiniteOnboardingEmails
              customerId={account.customerId}
              customerEmail={customerEmail}
              hasConciergeRep={!!account.customerServiceRep}
            />
          )}
        </>
      )}
      {currentTab === 'benefits-overview' && <LuxLoyaltyBenefitsOverview account={account} />}
      {currentTab === 'activities' && <LuxLoyaltyActivityLog account={account} />}
    </Stack>
  );
}
